import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import MaterialTable from "material-table";

import tableStyles from "../../../assets/jss/material-dashboard-pro-react/modules/commonTableStyles";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import Button from "../../../components/CustomButtons/Button";
import SafeMTablePagination from "../../../components/Table/SafeMTablePagination";
import { Switch, makeStyles } from "../../../utils/barrels/material-ui-core";
import {
	Add,
	ArrowDownward,
	ArrowUpward,
	Edit,
	Delete,
	FirstPage,
	LastPage,
	SkipNext,
	SkipPrevious,
} from "../../../utils/barrels/material-ui-icons";

const styles = {
	...tableStyles,
};

const useStyles = makeStyles(styles);

const createPaginationComponent = ({ classes }) => {
	const PaginationComponent = (propsNew) => (
		<SafeMTablePagination classes={classes} {...propsNew} />
	);
	PaginationComponent.displayName = "PaginationComponent";
	return PaginationComponent;
};

export default function RbSelect(props) {
	const classes = useStyles();
	const { t } = useTranslation();
	const [loader] = useState(false);
	const [data, setData] = useState([]);
	const { rules_data } = props;
	const tableRef = useRef(null);

	useEffect(() => {
		const prepare_data = rules_data.map((res) => {
			const {
				shipment_type,
				mode,
				min_invoice_value,
				max_invoice_value,
				min_weight,
				max_weight,
				destination_pincodes,
				pickup_pincodes,
				from_zone,
				to_zone,
				is_appointment_taken,
			} = res;
			const criteria = {};
			if (shipment_type && !shipment_type.every((val) => val === "")) {
				criteria["order_type"] = shipment_type;
			}
			if (mode && !mode.every((val) => val === "")) {
				criteria["mode"] = mode;
			}
			if (
				destination_pincodes &&
				!destination_pincodes.every((val) => val === "")
			) {
				criteria["destination_pincode"] = destination_pincodes;
			}

			if (pickup_pincodes && !pickup_pincodes.every((val) => val === "")) {
				criteria["source_pincode"] = pickup_pincodes;
			}
			if (from_zone && !from_zone.every((val) => val === "")) {
				criteria["from_zone"] = from_zone;
			}
			if (to_zone && !to_zone.every((val) => val === "")) {
				criteria["to_zone"] = to_zone;
			}

			if (min_invoice_value || max_invoice_value) {
				criteria["invoice"] = {
					min: min_invoice_value,
					max: max_invoice_value,
				};
			}
			if (min_weight || max_weight) {
				criteria["weight"] = { min: min_weight, max: max_weight };
			}
			if (is_appointment_taken) {
				criteria["is_appointment_taken"] = is_appointment_taken;
			}
			return {
				...res,
				criteria,
			};
		});
		setData(prepare_data);
	}, [rules_data]);

	const change_priority = (newindex, oldindex, ruleData) => {
		setData((prevState) => {
			const dataCopy = [...prevState];
			dataCopy[oldindex] = dataCopy.splice(newindex, 1, dataCopy[oldindex])[0];
			const body = {
				carrier_setting_id: ruleData.carrier_setting_id
					? ruleData.carrier_setting_id
					: 0,
				priority: dataCopy.map((val) => val.id),
			};
			props
				.updateRule(body)
				.then(() => {
					props.setBackdrop(false);
					return dataCopy;
				})
				.catch((err) => {
					props.setBackdrop(false);
					console.error(err);
					return prevState;
				});
			return dataCopy;
		});
	};

	const handleChange = (ruleData) => {
		props.setBackdrop(true);
		const body = {
			is_active: !ruleData.is_active,
			rb_select_rule_id: ruleData.id ? ruleData.id : 0,
		};
		props
			.updateRule(body)
			.then(() => {
				props.setBackdrop(false);
				setData((prevState) => {
					const temp = [...prevState];
					temp[ruleData.tableData.id].is_active = !ruleData.is_active;
					return temp;
				});
			})
			.catch((err) => {
				props.setBackdrop(false);
				console.error(err);
			});
	};
	return (
		<Card>
			<CardHeader color="primary" icon>
				<h5 className={classes.cardIconTitle}>{t("rb_select")}</h5>
			</CardHeader>
			<CardBody>
				<div className={classes.cardAddButton}>
					<Link to="/client/create-rules">
						<Button color="primary">
							<Add />
							{t("create_new_rule")}
						</Button>
					</Link>
				</div>
			</CardBody>
			<MaterialTable
				icons={{
					FirstPage,
					LastPage,
					PreviousPage: SkipPrevious,
					NextPage: SkipNext,
				}}
				localization={{
					header: {
						actions: t("actions"),
					},
				}}
				tableRef={tableRef}
				columns={[
					{
						title: t("priority_label"),
						render: (r_data) => (
							<div>
								{r_data.tableData.id > 0 && (
									<ArrowUpward
										style={{ color: "#6F57E9", cursor: "pointer" }}
										onClick={() => {
											change_priority(
												r_data.tableData.id - 1,
												r_data.tableData.id,
												r_data
											);
										}}
									/>
								)}
								{data.length - 1 !== r_data.tableData.id && (
									<ArrowDownward
										style={{ cursor: "pointer" }}
										onClick={() => {
											change_priority(
												r_data.tableData.id + 1,
												r_data.tableData.id,
												r_data
											);
										}}
									/>
								)}
							</div>
						),
						sorting: false,
						searchable: false,
					},
					{
						title: t("rule_name"),
						field: "rule_name",
						sorting: false,
						searchable: false,
					},
					{
						title: t("added_criteria"),
						field: "criteria",
						render: (rowData) => (
							<span>
								{Object.keys(rowData.criteria).map((criteria, i, ary) => (
									<Fragment key={criteria}>
										{t(criteria)}
										{ary.length - 1 !== i && "/"}
									</Fragment>
								))}
							</span>
						),
						sorting: false,
						searchable: false,
					},
					{
						title: t("courier"),
						field: "contact",
						sorting: false,
						searchable: false,
					},
					{
						title: t("active"),
						field: "contact",
						render: (rowData) => (
							<Switch
								checked={rowData.is_active}
								onChange={() => handleChange(rowData)}
								name="checkedA"
								inputProps={{ "aria-label": "secondary checkbox" }}
								color="primary"
							/>
						),
						sorting: false,
						searchable: false,
					},
				]}
				title=""
				options={{
					pageSize: 10,
					filtering: false,
					search: false,
					actionsColumnIndex: -1,
					toolbar: false,
					padding: "dense",
				}}
				isLoading={loader}
				actions={[
					() => ({
						icon: Edit,
						tooltip: t("edit_rule"),
						onClick: (event, rowData) => {
							props.setCarrierSettingEditData(rowData);
							props.history.push(`/client/edit-rule/${rowData.rule_name}`);
							props.setCarrierSettingId(rowData.carrier_setting_id);
							props.setRulePriority(rowData.priority);
						},
					}),
					() => ({
						icon: Delete,
						tooltip: t("delete_rule"),
					}),
				]}
				data={data}
				components={useMemo(
					() => ({
						Pagination: createPaginationComponent({ classes }),
					}),
					[classes]
				)}
			/>
		</Card>
	);
}
